import React, { useState, useEffect, useContext } from 'react';
import { fileUrl } from '../utils/fileUrl';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { User, Star, Phone, Car, FileText, Image } from 'lucide-react';

const DriverProfile = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    phone: '',
    car_type: '',
    license_plate: '',
    profile_photo_url: '',
    bio: ''
  });
  const [ratings, setRatings] = useState([]);
  const [photoPreview, setPhotoPreview] = useState('');
  const isDriver = user?.role === 'driver';

  useEffect(() => {
    fetchProfile();
    if (isDriver) {
      fetchRatings();
    }
  }, []);

  const fetchProfile = async () => {
    try {
      let userResponse;
      if (isDriver) {
        userResponse = await axios.get(`${API}/profile/driver/${user.id}`);
      } else {
        // For admins, get user data directly
        userResponse = await axios.get(`${API}/users`);
        const currentUser = userResponse.data.find(u => u.id === user.id);
        userResponse = { data: currentUser };
      }
      
      setProfile({
        name: userResponse.data.name || '',
        phone: userResponse.data.phone || '',
        car_type: userResponse.data.car_type || '',
        license_plate: userResponse.data.license_plate || '',
        profile_photo_url: userResponse.data.profile_photo_url || '',
        bio: userResponse.data.bio || ''
      });
      setLoading(false);
    } catch (error) {
      console.error('Failed to load profile');
      setLoading(false);
    }
  };

  const fetchRatings = async () => {
    try {
      const response = await axios.get(`${API}/driver/${user.id}/ratings`);
      setRatings(response.data);
    } catch (error) {
      console.error('Failed to load ratings');
    }
  };

  const handleChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WebP image');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/profile-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setProfile({ ...profile, profile_photo_url: response.data.photo_url });
      toast.success('Photo uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
      setPhotoPreview('');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/profile/driver`, profile);
      toast.success('Profile updated successfully!');
      // Reload page to update header profile picture
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
      setSaving(false);
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <Star
        key={index}
        className={`w-4 h-4 ${index < rating ? 'fill-gold-400 text-gold-400' : 'text-gray-300'}`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <h1 className="text-3xl font-bold">My Profile</h1>

      {/* Profile Edit Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-6 h-6" />
            Edit Your Profile
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={profile.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Contact Phone
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={profile.phone}
                  onChange={handleChange}
                />
              </div>
            </div>

            {isDriver && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="car_type">
                    <Car className="w-4 h-4 inline mr-1" />
                    Car Type *
                  </Label>
                  <Input
                    id="car_type"
                    name="car_type"
                    placeholder="e.g., Black SUV, Sedan"
                    value={profile.car_type}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="license_plate">License Plate *</Label>
                  <Input
                    id="license_plate"
                    name="license_plate"
                    placeholder="ABC1234"
                    value={profile.license_plate}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>
                <Image className="w-4 h-4 inline mr-1" />
                Profile Photo
              </Label>
              
              {/* Photo Preview */}
              <div className="flex items-center gap-4">
                {(photoPreview || profile.profile_photo_url) && (
                  <img
                    src={photoPreview || fileUrl(profile.profile_photo_url)}
                    alt="Profile preview"
                    className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                    onError={(e) => {
                      console.error('Image failed to load:', e.target.src);
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <div className="flex-1">
                  <Input
                    id="profile_photo"
                    type="file"
                    accept="image/jpeg,image/png,image/jpg,image/webp"
                    onChange={handleFileChange}
                    disabled={uploading}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {uploading ? 'Uploading...' : 'JPEG, PNG, or WebP (max 5MB)'}
                  </p>
                </div>
              </div>

              {/* Optional: Still allow URL input */}
              <details className="mt-2">
                <summary className="text-sm text-gray-600 cursor-pointer">Or enter photo URL</summary>
                <Input
                  id="profile_photo_url"
                  name="profile_photo_url"
                  type="text"
                  placeholder="https://example.com/your-photo.jpg"
                  value={profile.profile_photo_url}
                  onChange={handleChange}
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty if you uploaded a file above</p>
              </details>
            </div>

            {isDriver && (
              <div className="space-y-2">
                <Label htmlFor="bio">
                  <FileText className="w-4 h-4 inline mr-1" />
                  Bio / About You
                </Label>
                <Textarea
                  id="bio"
                  name="bio"
                  placeholder="Tell riders about yourself, your experience, and what makes you a great driver..."
                  value={profile.bio}
                  onChange={handleChange}
                  rows={4}
                />
              </div>
            )}

            <Button type="submit" disabled={saving} className="w-full">
              {saving ? 'Saving...' : 'Save Profile'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Ratings Display - Only for Drivers */}
      {isDriver && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-6 h-6 text-gold-400" />
              Your Ratings
            </CardTitle>
          </CardHeader>
          <CardContent>
            {ratings.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No ratings yet. Complete rides to receive ratings from riders!</p>
            ) : (
              <div className="space-y-4">
                {ratings.map((rating) => (
                  <div key={rating.id} className="border-b pb-4 last:border-b-0">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium">{rating.rider_name}</div>
                      <div className="flex items-center gap-1">
                        {renderStars(rating.rating)}
                      </div>
                    </div>
                    {rating.comment && (
                      <p className="text-sm text-gray-600">{rating.comment}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(rating.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DriverProfile;
